<template>
  <content-dialog
    :visible.sync="modalVisible"
    persistent
    max-width="1000px"
  >
    <template slot="dialogTitle">
      <v-row no-gutters>
        <v-col>
          <span v-if="yachtEquipmentPackDirty.id">
            Edit Equipment Pack
          </span>
          <span v-else>
            Add New Equipment Pack
          </span>
        </v-col>
        <v-col
          v-if="!yachtEquipmentPackDirty.custom"
          cols="auto"
        >
          <v-btn-toggle
            v-model="yachtEquipmentPackDirty.status"
            class="ml-3"
            mandatory
            color="primary"
          >
            <v-btn
              value="draft"
              small
            >
              Draft
            </v-btn>
            <v-btn
              value="active"
              small
            >
              Active
            </v-btn>
            <v-btn
              value="discarded"
              small
            >
              Discarded
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </template>

    <v-card-text :class="{'px-5 pt-6 pb-12': !$vuetify.breakpoint.xsOnly }">
      <validation-observer ref="validationObserver">
        <!--<editor-fold desc="Installation">-->
        <v-subheader>
          Installed By
        </v-subheader>
        <validation-provider
          v-slot="v"
          rules="required"
          name="Installed By"
        >
          <v-btn-toggle
            v-model="yachtEquipmentPackDirty.installed_by"
            class="elevation-1 row no-gutters v-btn-toggle--block"
            color="white"
            :class="{ 'error': v.errors.length > 0 }"
            data-vv-as="installed by"
            borderless
          >
            <v-btn
              value="shipyard"
              :class="{ 'primary': yachtEquipmentPackDirty.installed_by === 'shipyard' }"
              large
              class="flex-grow-1"
            >
              Shipyard
            </v-btn>
            <v-btn
              value="navigare"
              :class="{ 'primary': yachtEquipmentPackDirty.installed_by === 'navigare' }"
              large
              class="flex-grow-1"
            >
              Navigare Base
            </v-btn>
            <v-btn
              value="contractor"
              :class="{ 'primary': yachtEquipmentPackDirty.installed_by === 'contractor' }"
              large
              class="flex-grow-1"
            >
              C. Yard
            </v-btn>
          </v-btn-toggle>

          <div class="caption px-2 my-2 error--text">
            {{ v.errors[0] }}
          </div>
        </validation-provider>
        <!--</editor-fold>-->

        <v-subheader class="mt-2">
          Basic Details
        </v-subheader>

        <validation-provider
          v-if="custom"
          v-slot="v"
          rules="required"
          name="Pack Label"
        >
          <v-text-field
            v-if="custom"
            v-model="yachtEquipmentPackDirty.label"
            label="Pack Label"
            placeholder="e.g. Special equipment"
            outlined
            :error-messages="v.errors"
          />
        </validation-provider>

        <validation-provider
          v-else
          v-slot="v"
          rules="required"
          name="Pack Label"
        >
          <v-text-field
            v-model="yachtEquipmentPackDirty.label"
            name="label"
            label="Pack Label"
            placeholder="e.g. Lagoon 40/45 basic Navigare pack"
            hint="Staff will identify the pack by this label"
            outlined
            data-vv-as="pack label"
            :error-messages="v.errors"
          />
        </validation-provider>

        <validation-provider
          v-slot="v"
          rules="required"
          name="Pack Public Label"
        >
          <v-text-field
            v-model="yachtEquipmentPackDirty.public_label"
            name="public_label"
            label="Pack Public Label"
            placeholder="e.g. Lagoon 40/45 basic Navigare pack"
            hint="This label is used in materials for yacht owner"
            outlined
            :error-messages="v.errors"
          />
        </validation-provider>

        <v-text-field
          v-model="yachtEquipmentPackDirty.code"
          name="code"
          label="Pack Code"
          outlined
          data-vv-as="pack code"
        />

        <validation-provider
          v-slot="v"
          rules="required"
          name="Category"
        >
          <v-autocomplete
            v-model="categoryDirty"
            :items="categories.data"
            :loading="categories.loading"
            name="category"
            label="Select Category"
            item-value="id"
            item-text="name"
            clearable
            autocomplete="off"
            outlined
            :error-messages="v.errors"
          />
        </validation-provider>

        <!--<editor-fold desc="Items">-->
        <v-row
          no-gutters
          align="center"
          class="mb-2"
        >
          <v-col>
            <v-subheader>
              Items
            </v-subheader>
          </v-col>
          <v-col class="shrink">
            <v-select
              v-model="yachtEquipmentPackDirty.currency"
              :items="['EUR', 'USD', 'GBP', 'SEK']"
              class="v-text-field--no-border"
              label="Currency"
              style="width: 120px"
              outlined
              flat
              dense
              hide-details
            />
          </v-col>
        </v-row>
        <item-list-table
          :items.sync="yachtEquipmentPackDirty.items"
          :currency="yachtEquipmentPackDirty.currency"
          :purchase-price.sync="yachtEquipmentPackDirty.purchase_price"
          :sale-price.sync="yachtEquipmentPackDirty.sale_price"
        />
        <!--</editor-fold>-->
        <div v-if="!custom">
          <v-subheader class="mt-6">
            Items configurable for sales
          </v-subheader>

          <v-switch
            v-model="yachtEquipmentPackDirty.is_configurable"
            label="Yes, allow configuring this equipment pack"
            hint="If selected, in yacht configuration for sales each item can be added/removed"
            persistent-hint
            class="ma-0 pa-0"
          />

          <!--<editor-fold desc="Applicable Yacht Models">-->
          <v-subheader class="mt-6">
            Applicable Yacht Models
          </v-subheader>

          <validation-provider
            v-slot="v"
            rules="required"
            name="Applicable Yacht Models"
          >
            <v-autocomplete
              v-model="yachtEquipmentPackDirty.models"
              :items="yachtModels.data"
              :loading="yachtModels.loading"
              name="models"
              label="Select Yacht Models"
              item-text="full_label"
              item-value="id"
              return-object
              outlined
              single-line
              small-chips
              deletable-chips
              multiple
              :error-messages="v.errors"
              :hide-details="yachtEquipmentPackDirty.models.length > 0"
            >
              <template v-slot:item="{ item }">
                <v-list-item-avatar>
                  <img
                    v-if="item.main_image"
                    :src="item.main_image.path"
                  >
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.full_label }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span class="grey--text">#{{ item.id }}</span> &middot;
                    <strong>{{ item.model_year }}</strong> &middot;
                    {{ item.cabin_count_description }} cabins &middot;
                    {{ item.berth_count_description }} berths &middot;
                    {{ item.engines_description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </validation-provider>
          <div
            v-show="yachtEquipmentPackDirty.models.length > 0"
            class="mb-6 mt-4"
          >
            <template v-for="yachtModel in yachtEquipmentPackDirty.models">
              <v-row
                :key="yachtModel.id"
                dense
                no-gutters
                align="center"
                class="list--bordered py-2"
              >
                <v-col
                  cols="auto"
                >
                  <v-icon
                    color="red"
                    @click="removeSelectedYachtModel(yachtModel.id)"
                  >
                    close
                  </v-icon>
                </v-col>
                <v-col
                  class="subtitle-1 grow"
                >
                  {{ yachtModel.label }}
                </v-col>
                <v-col
                  class="grey--text caption mr-4"
                  cols="auto"
                >
                  add by default
                </v-col>
                <v-col cols="auto">
                  <v-switch
                    v-model="yachtModel.default"
                    hide-details
                    class="ma-0 pa-0"
                  />
                </v-col>
              </v-row>
            </template>
          </div>
        </div>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Applicable Countries">-->
        <div v-if="!custom">
          <v-subheader>
            Applicable Countries
          </v-subheader>

          <validation-provider
            v-slot="v"
            rules="required"
            name="Applicable Countries"
          >
            <v-autocomplete
              v-model="yachtEquipmentPackDirty.countries"
              :items="countries.data"
              :loading="countries.loading"
              label="Select Countries"
              item-value="id"
              item-text="name"
              autocomplete="off"
              return-object
              single-line
              outlined
              small-chips
              deletable-chips
              multiple
              :error-messages="v.errors"
            />
          </validation-provider>
        </div>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Supplier">-->
        <v-subheader>
          Equipment Supplier
        </v-subheader>
        <v-autocomplete
          v-model="yachtEquipmentPackDirty.supplier_id"
          outlined
          label="Select supplier"
          clearable
          :items="suppliers.data"
          item-text="name"
          item-value="id"
          :loading="suppliers.loading"
        />
        <!--</editor-fold>-->

        <!--<editor-fold desc="Note">-->
        <v-subheader>
          Note
        </v-subheader>
        <v-textarea
          v-model="yachtEquipmentPackDirty.note"
          outlined
          single-line
        />
        <!--</editor-fold>-->

        <v-subheader>
          Offer Visibility Settings
        </v-subheader>

        <v-checkbox
          v-model="yachtEquipmentPackDirty.offer_visible"
          label="Pack listed"
          class="ma-0"
        />

        <v-checkbox
          v-model="yachtEquipmentPackDirty.offer_items_visible"
          label="Pack's items listed"
          class="ma-0"
        />
      </validation-observer>
    </v-card-text>

    <template slot="dialogActions">
      <v-btn
        v-if="yachtEquipmentPackDirty.id"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Yacht Equipment Pack?"
      @confirmed="deleteYachtEquipmentPack"
    />
  </content-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import YachtModel from '@/models/YachtModel'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import YachtEquipmentPackCategory from '@/models/YachtEquipmentPackCategory'
import Contractor from '@/models/Contractor'
import ContentDialog from '@/components/helpers/Dialog'
import ItemListTable from './ItemListTable'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import validationScrollMixin from '@/mixins/validationScrollMixin'

export default {
    name: 'YachtEquipmentPackEditDialog',
    components: {
      ContentDialog,
      ItemListTable,
      ConfirmDeleteDialog
    },
    mixins: [deleteConfirmationMixin, validationScrollMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      yachtEquipmentPack: {
        type: Object,
        default: () => YachtEquipmentPack.emptyModel
      },
      custom: {
        type: Boolean,
        default: false
      },
      editingCustom: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        yachtEquipmentPackDirty: cloneDeep(this.yachtEquipmentPack),
        automaticMarkup: null,
        yachtModels: {
          data: [],
          loading: false
        },
        contractors: {
          data: [],
          loading: false
        },
        suppliers: {
          data: [],
          loading: false
        },
        // categories: {
        //   data: [],
        //   loading: false
        // },
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      countries() {
        return this.$store.getters['settings/charteringCountries']
      },
      categories() {
        return this.$store.getters['settings/equipmentPackCategories']
      },
      categoryDirty: {
        get: function () {
          return this.yachtEquipmentPackDirty.yacht_equipment_pack_category_id
        },
        set: function (value) {
          this.yachtEquipmentPackDirty.yacht_equipment_pack_category_id = value || null
        }
      }
    },
    watch: {
      modalVisible() {
        if (!this.visible) return

        this.yachtEquipmentPackDirty = cloneDeep(this.yachtEquipmentPack)

        if (this.custom) {
          this.yachtEquipmentPackDirty.status = YachtEquipmentPack.STATUS_ACTIVE
          this.yachtEquipmentPackDirty.custom = true
        }
      }
    },
    created() {
      this.init()
    },
    methods: {
      init() {
        this.fetchContractors()
        this.fetchSuppliers()
        if (!this.custom) {
          this.fetchYachtModels()
        }

        this.yachtEquipmentPackDirty = cloneDeep(this.yachtEquipmentPack)
        // this.fetchCategories()
      },
      async submit() {
        let validate = await this.$refs.validationObserver.validate()

        if (!validate) {
          this.showAlertAndScrollToFirstError()
          return
        }

        this.save()
      },
      async save() {
        this.submitting = true

        let formData = {
          label: this.yachtEquipmentPackDirty.label,
          public_label: this.yachtEquipmentPackDirty.public_label,
          code: this.yachtEquipmentPackDirty.code,
          purchase_price: this.yachtEquipmentPackDirty.purchase_price,
          sale_price: this.yachtEquipmentPackDirty.sale_price,
          currency: this.yachtEquipmentPackDirty.currency,
          items: this.mapItems(),
          note: this.yachtEquipmentPackDirty.note,
          custom: this.custom,
          yacht_equipment_pack_category_id: this.yachtEquipmentPackDirty.yacht_equipment_pack_category_id,
          category: this.custom ? this.categories.data.find(c => c.id === this.yachtEquipmentPackDirty.yacht_equipment_pack_category_id) : undefined,
          is_configurable: this.yachtEquipmentPackDirty.is_configurable,
          offer_visible: this.yachtEquipmentPackDirty.offer_visible,
          offer_visible_sale_price: this.yachtEquipmentPackDirty.offer_visible_sale_price,
          offer_items_visible: this.yachtEquipmentPackDirty.offer_items_visible,
          offer_items_visible_sale_price: this.yachtEquipmentPackDirty.offer_items_visible_sale_price,
          installed_by: this.yachtEquipmentPackDirty.installed_by,
          supplier_id: this.yachtEquipmentPackDirty.supplier_id,
          countries: this.yachtEquipmentPackDirty.countries && this.yachtEquipmentPackDirty.countries.length
            ? this.mapCountryIds() : [],
          yacht_models: this.yachtEquipmentPackDirty.models && this.yachtEquipmentPackDirty.models.length
            ? this.mapYachtModels() : [],
          status: this.yachtEquipmentPackDirty.status
        }

        try {
          let equipmentPack = await YachtEquipmentPack.api.save(formData, this.yachtEquipmentPackDirty.id)

          this.$store.dispatch('alert', {
            'text': this.custom ? 'Custom Yacht Equipment Pack Saved' : 'Yacht Equipment Pack Saved',
            'color': 'success'
          })

          this.$emit(this.yachtEquipmentPackDirty.id ? 'yachtEquipmentPack:updated' : 'yachtEquipmentPack:created', equipmentPack)

          // this.yachtEquipmentPackDirty = cloneDeep(YachtEquipmentPack.emptyModel)
          this.modalVisible = false
        } catch (e) {
          throw e
        } finally {
          this.submitting = false
        }
      },
      async deleteYachtEquipmentPack() {
        this.deleting = true

        try {
          await YachtEquipmentPack.api.destroy(this.yachtEquipmentPackDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Yacht equipment pack deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('yachtEquipmentPack:deleted', this.yachtEquipmentPackDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      },
      async fetchYachtModels() {
        this.yachtModels.loading = true

        try {
          this.yachtModels.data = await YachtModel.api.fetch({
            status: YachtModel.STATUS_ACTIVE,
            is_sales_active: true
          })
        } catch (e) {
          throw e
        } finally {
          this.yachtModels.loading = false
        }
      },
      async fetchCategories() {
        this.categories.loading = true

        try {
          this.categories.data = await YachtEquipmentPackCategory.api.fetch()
        } catch (e) {
          throw e
        } finally {
          this.categories.loading = false
        }
      },
      async fetchContractors() {
        this.contractors.loading = true

        try {
          this.contractors.data = await Contractor.api.fetch({ type: Contractor.TYPE_YARD })
        } catch (e) {
          throw e
        } finally {
          this.contractors.loading = false
        }
      },
      async fetchSuppliers() {
        this.suppliers.loading = true

        try {
          this.suppliers.data = await Contractor.api.fetch({ type: Contractor.TYPE_SUPPLIER })
        } catch (e) {
          throw e
        } finally {
          this.suppliers.loading = false
        }
      },
      removeSelectedYachtModel(id) {
        this.yachtEquipmentPackDirty.models.splice(this.yachtEquipmentPackDirty.models.findIndex(model => id === model.id), 1)
      },
      removeCustomPack() {
        this.$store.dispatch('alert', {
          'text': 'Custom Yacht Equipment Pack Removed',
          'color': 'info'
        })
        this.$emit('update:removeCustomPack')
        this.yachtEquipmentPackDirty = cloneDeep(YachtEquipmentPack.emptyModel)
        this.modalVisible = false
      },
      mapCountryIds() {
        return this.yachtEquipmentPackDirty.countries.map(country => country.id)
      },
      mapYachtModels() {
        return this.yachtEquipmentPackDirty.models.map(ym => {
          return { yacht_model_id: ym.id, default: ym.default || false }
        })
      },
      mapItems() {
        return this.yachtEquipmentPackDirty.items.map(item => {
          return {
            label: item.label,
            code: item.code,
            quantity: item.quantity,
            unit_purchase_price: item.unit_purchase_price,
            unit_sale_price: item.unit_sale_price,
            purchase_price: item.purchase_price,
            sale_price: item.sale_price
          }
        })
      }
    }
  }
</script>
